import { useState, useRef } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { Todo, TeamWithMembers } from '../../types';
import type { TodoFormRef } from '../../components/TodoForm';
import type { ViewType } from '../../components/ViewTabs';

export interface ModalState {
  // View state
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;

  // Todo/Form state
  selectedTodo: Todo | null;
  setSelectedTodo: (todo: Todo | null) => void;
  showTodoForm: boolean;
  setShowTodoForm: (show: boolean) => void;
  todoFormRef: React.RefObject<TodoFormRef>;

  // Modal stack for modal return functionality
  modalStack: {
    previousModal?: string;
    previousPhase?: string;
    previousState?: Record<string, unknown>;
  };
  setModalStack: (stack: { previousModal?: string; previousPhase?: string; previousState?: Record<string, unknown> }) => void;

  // Child task state
  addingChildToParentId: string | null;
  setAddingChildToParentId: (id: string | null) => void;

  // Search/Filter visibility state
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Pomodoro state
  showPomodoroTimer: boolean;
  setShowPomodoroTimer: (show: boolean) => void;
  currentPomodoroTask: Todo | null;
  setCurrentPomodoroTask: (task: Todo | null) => void;

  // AI Assistant state
  showAIAssistant: boolean;
  setShowAIAssistant: (show: boolean) => void;

  // Team state
  selectedTeamId: string | null;
  setSelectedTeamId: (id: string | null) => void;
  showTeamModal: boolean;
  setShowTeamModal: (show: boolean) => void;
  showJoinTeamModal: boolean;
  setShowJoinTeamModal: (show: boolean) => void;
  editingTeam: TeamWithMembers | null;
  setEditingTeam: (team: TeamWithMembers | null) => void;
}

export function useModalState(): ModalState {
  // View state
  const [activeView, setActiveView] = useLocalStorage<ViewType>('todoApp_activeView', 'week');

  // Todo/Form state
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [showTodoForm, setShowTodoForm] = useState(false);
  const todoFormRef = useRef<TodoFormRef>(null);

  // Modal stack state
  const [modalStack, setModalStack] = useState<{
    previousModal?: string;
    previousPhase?: string;
    previousState?: Record<string, unknown>;
  }>({});

  // Child task state
  const [addingChildToParentId, setAddingChildToParentId] = useState<string | null>(null);

  // Search/Filter visibility state
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pomodoro state
  const [showPomodoroTimer, setShowPomodoroTimer] = useState(false);
  const [currentPomodoroTask, setCurrentPomodoroTask] = useState<Todo | null>(null);

  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  // Team state
  const [selectedTeamId, setSelectedTeamId] = useLocalStorage<string | null>('todoApp_selectedTeamId', null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamWithMembers | null>(null);

  return {
    activeView,
    setActiveView,
    selectedTodo,
    setSelectedTodo,
    showTodoForm,
    setShowTodoForm,
    todoFormRef,
    modalStack,
    setModalStack,
    addingChildToParentId,
    setAddingChildToParentId,
    showFilters,
    setShowFilters,
    searchQuery,
    setSearchQuery,
    showPomodoroTimer,
    setShowPomodoroTimer,
    currentPomodoroTask,
    setCurrentPomodoroTask,
    showAIAssistant,
    setShowAIAssistant,
    selectedTeamId,
    setSelectedTeamId,
    showTeamModal,
    setShowTeamModal,
    showJoinTeamModal,
    setShowJoinTeamModal,
    editingTeam,
    setEditingTeam
  };
}
